import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAtom } from 'jotai';
import { searchValuesAtom } from '../../Atoms';
import useAPI from '../../api';
import { download } from '../../api/utils';
import SectionPage from '../../Components/Paper/SectionPage';
import mapFilters from '../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import ErrorDialog from '../../Components/Error';
import PurchaseListItem from './Components/PurchaseListItem';
import PurchaseFilters from './Components/PurchaseFilters';
import PurchaseDialog from './Components/PurchaseDialog';
import { PURCHASE_SORTING } from './Utils/constants';
import { useOutletContext } from 'react-router-dom';
import useHasPermission from '../../Hooks/useHasPermission';
import useNotify from '../../Hooks/useNotify';

const getPurchaseListItem = (
  data,
  onClick,
  onDelete,
  onSendPurchaseRequest,
  onAcceptPurchaseRequest,
  userInfo
) => (
  <PurchaseListItem
    key={data.id}
    data={data}
    onClick={onClick}
    onDelete={onDelete}
    onSendPurchaseRequest={onSendPurchaseRequest}
    onAcceptPurchaseRequest={onAcceptPurchaseRequest}
    userInfo={userInfo}
  />
);
const PurchaseHistoryPage = function PurchaseHistoryPage() {
  const API = useAPI();
  const intl = useIntl();
  const userInfo = useOutletContext() || {};

  const [purchaseDialog, setPurchaseDialog] = useState(null);
  const [unitPrice, setUnitPrice] = useState(0);
  const [error, setError] = useState(null);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const { notify, notifyError } = useNotify();

  const openNewPurchaseDialog = () => {
    setPurchaseDialog(true);
  };
  const closeNewPurchaseDialog = () => {
    setPurchaseDialog(null);
  };

  const openEditPurchaseDialog = (purchaseData) => {
    setPurchaseDialog(purchaseData);
  };

  useEffect(() => {
    API.getUnitPrice((smsUnitPrice) => {
      setUnitPrice(smsUnitPrice);
    });
  }, []);

  const downloadAllPurchaseFile = useCallback(
    () =>
      download(
        'SMSPurchases',
        API.export2ExcelSMSPurchases({
          pageNumber: 0,
          filterExpression: mapFilters(
            ['Id'],
            [{ keyName: 'Id' }],
            [],
            searchValues
          ),
          sortExpression: [
            {
              sortValue: searchValues?.sortValue.value,
              sort: searchValues?.sortValue.direction,
            },
          ],
          silent: false,
        })
      ),
    [searchValues, error]
  );

  const getPurchaseHistory = useCallback(
    (pageNumber) =>
      API.getPurchaseHistory(
        {
          pageNumber,
          silent: pageNumber > 1,
          filterExpression: mapFilters(
            ['userFullName'],
            [{ keyName: 'state' }],
            [],
            searchValues
          ),
          sortExpression: [
            {
              sortValue: searchValues?.sortValue?.value,
              sort: searchValues?.sortValue?.direction,
            },
          ],
        },
        () => {
          saveFiltersInLocal(searchValues);
        },
        (err) => {
          setError(err);
        }
      ),

    [searchValues]
  );

  const {
    ObserverWrapper,
    setNewItems,
    recordCount,
    updateItem,
    removeClickedItem,
  } = useInfiniteQuery(getPurchaseHistory);

  const handleSubmit = (data) => {
    if (data.id === 'new-purchase') {
      const { id, ...rest } = data;
      API.createSMSPurchase(
        rest,
        (newPurchaseData) => {
          setNewItems(newPurchaseData, recordCount + 1);
          setPurchaseDialog(null);
          notify('NEW_PURCHASE_CREATE_SUCCESSFULLY', 'success');
        },
        (err) => {
          notifyError('CREATE_PURCHASE_ERROR', err);
        }
      );
    } else {
      API.editSMSPurchase(
        data,
        (response) => {
          updateItem(response);
          setPurchaseDialog(null);
          notify('PURCHASE_EDITED_SUCCESSFULLY', 'success');
        },
        (err) => {
          notifyError('EDITING_PURCHASE_ERROR', err);
        }
      );
    }
  };

  const sendPurchaseRequest = (id) => {
    API.updateSMSPurchaseStateToWaiting(
      id,
      (response) => {
        updateItem(response);
        notify('NEW_PURCHASE_SEND_SUCCESSFULLY', 'success');
      },
      (err) => {
        notifyError('SEND_NEW_PURCHASE_ERROR', err);
      }
    );
  };

  const acceptPurchaseRequest = (id) => {
    API.updateSMSPurchaseStateToProcessing(
      id,
      (response) => {
        updateItem(response);
        notify('NEW_PURCHASE_ACCEPT_SUCCESSFULLY', 'success');
      },
      (err) => {
        notifyError('ACCEPT_NEW_PURCHASE_ERROR', err);
      }
    );
  };

  const handleDeletePurchase = (id) => {
    API.deleteSMSPurchase(
      id,
      () => {
        removeClickedItem(id, recordCount - 1);
        notify('PURCHASE_DELETED', 'success');
      },
      () => {
        notify('PURCHASE_DELETE_ERROR', 'error');
      }
    );
  };

  const handleChangesStatusFilters = (newPurchaseState) => {
    setSearchValues((prev) => ({
      ...prev,
      ...{ state: newPurchaseState },
    }));
  };
  const isAdminOrSuperVisor = useHasPermission('purchases.create');
  return (
    <>
      <SectionPage
        sectionName="PURCHASE_HISTORY_CREDITS_SMS"
        sectionSubName="CREDITS"
        fab={isAdminOrSuperVisor ? intl.messages.NEW_PURCHASE : null}
        onClickFab={isAdminOrSuperVisor ? openNewPurchaseDialog : null}
        fabVariant="extended"
        ListObserverWrapper={ObserverWrapper}
        customEntryFunction={(item) =>
          getPurchaseListItem(
            item,
            openEditPurchaseDialog,
            handleDeletePurchase,
            sendPurchaseRequest,
            acceptPurchaseRequest,
            userInfo
          )
        }
        customFilters={
          <PurchaseFilters
            defaultValue={searchValues?.state || null}
            onChangeFilters={handleChangesStatusFilters}
            userInfo={userInfo}
          />
        }
        sortingOptions={PURCHASE_SORTING}
        filters={[{ keyName: 'state' }]}
        ExportToExcel={downloadAllPurchaseFile}
      />
      <PurchaseDialog
        id="new-purchase"
        title={
          purchaseDialog?.id
            ? intl.messages.EDIT_PURCHASE
            : intl.messages.NEW_PURCHASE
        }
        open={Boolean(purchaseDialog)}
        purchaseData={purchaseDialog}
        handleClose={closeNewPurchaseDialog}
        onSubmit={handleSubmit}
        unitPrice={unitPrice}
      />
      <ErrorDialog error={error} handleRetry={() => setError(null)} />
    </>
  );
};

export default PurchaseHistoryPage;
